import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FadeIn, Overline } from "../components/Motion";

const STEPS = [
    { num: "01", title: "Diagnose", body: "We map your revenue surface area: ICP, channels, partnership gaps, monetization leaks, and team coverage. Two-week sprint, no fluff." },
    { num: "02", title: "Plug in", body: "We embed as your interim BD / distribution layer. Calendar, CRM, contracts, partner conversations — we work inside your stack, not adjacent to it." },
    { num: "03", title: "Close", body: "We open doors, structure terms, and close revenue-generating agreements. You see pipeline and signed deals — not slide decks." },
    { num: "04", title: "Systemize", body: "We document the BD motion that works for you: playbooks, sequences, partner economics. So when you do hire, the seat is set up to win." },
];

const ENGAGEMENT = [
    { tag: "Revenue share", body: "Aligned, outcome-driven economics on the deals we close. Real upside when we deliver." },
    { tag: "Deal-based", body: "Project-level economics for specific partnerships, sponsorships, or distribution launches." },
    { tag: "Light retainer", body: "Lean monthly retainer to cover access, embedded operating cadence, and momentum — never bloated." },
];

const SUCCESS = [
    "Strategic partnerships closed in 30–90 days",
    "New revenue channels unlocked and operating",
    "Clear, scalable monetization structure",
    "Stronger positioning for next funding round",
];

const HowWeWork = () => {
    return (
        <main data-testid="page-how-we-work" className="text-white">
            <section className="relative pt-40 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
                <div className="absolute inset-0 starfield opacity-30 fade-edges pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                    <FadeIn>
                        <Overline className="mb-8">How we work</Overline>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-[6.5rem] leading-[0.98] max-w-5xl">
                            A senior operator, <span className="italic text-accent">on demand.</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <p className="mt-10 max-w-2xl text-[15px] lg:text-lg leading-relaxed text-white/65">
                            We function as your BD and revenue layer — embedded into your team, working under your roadmap,
                            and accountable to closed outcomes. Quietly. Without the headcount.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* PROCESS */}
            <section className="relative pb-24 border-t border-white/[0.06]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 divide-y divide-white/[0.06]">
                    {STEPS.map((s, i) => (
                        <FadeIn key={s.num} delay={i * 0.05}>
                            <article
                                data-testid={`step-${s.num}`}
                                className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 py-14 lg:py-20"
                            >
                                <div className="md:col-span-3">
                                    <span className="font-display text-6xl lg:text-7xl text-white/25 leading-none">{s.num}</span>
                                </div>
                                <div className="md:col-span-4">
                                    <h2 className="font-display text-4xl lg:text-5xl leading-[1.05] text-white">{s.title}</h2>
                                </div>
                                <div className="md:col-span-5">
                                    <p className="text-[15px] lg:text-lg leading-relaxed text-white/65 max-w-xl">{s.body}</p>
                                </div>
                            </article>
                        </FadeIn>
                    ))}
                </div>
            </section>

            {/* ENGAGEMENT MODEL */}
            <section className="relative py-32 lg:py-40 border-t border-white/[0.06]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <FadeIn>
                        <Overline className="mb-4">Engagement model</Overline>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
                            Aligned with outcomes. <span className="italic text-accent">Not with hours.</span>
                        </h2>
                    </FadeIn>
                    <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.06]">
                        {ENGAGEMENT.map((e, i) => (
                            <FadeIn key={e.tag} delay={i * 0.07} className="bg-[#05070A]">
                                <div data-testid={`engagement-${i + 1}`} className="p-10 lg:p-14 h-full">
                                    <Overline className="mb-8">{e.tag}</Overline>
                                    <p className="font-display text-2xl lg:text-3xl leading-snug text-white/95">{e.body}</p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                    <FadeIn delay={0.2}>
                        <p className="mt-12 max-w-2xl text-[14px] leading-relaxed text-white/55">
                            Every engagement is structured so that if we don't drive results, we don't get paid meaningfully.
                            That is the model.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* SUCCESS */}
            <section className="relative py-32 lg:py-40 border-t border-white/[0.06]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-4">
                        <FadeIn>
                            <Overline>What success looks like</Overline>
                            <p className="mt-6 font-display text-3xl lg:text-4xl leading-tight text-white/90">
                                30 – 90 days from kickoff to closed outcomes.
                            </p>
                        </FadeIn>
                    </div>
                    <ul className="md:col-span-8 space-y-6">
                        {SUCCESS.map((line, i) => (
                            <FadeIn key={line} delay={i * 0.05}>
                                <li className="flex gap-6 items-start border-b border-white/[0.06] pb-6">
                                    <span className="font-display text-3xl text-white/30 leading-none">0{i + 1}</span>
                                    <span className="text-[16px] lg:text-lg text-white/80 leading-relaxed">{line}</span>
                                </li>
                            </FadeIn>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="relative py-32 lg:py-40 border-t border-white/[0.06]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
                    <div className="md:col-span-8">
                        <FadeIn>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-[4rem] leading-[1.05] text-white">
                                If you need revenue moving — <span className="italic text-white/75">let's talk.</span>
                            </h2>
                        </FadeIn>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link
                            to="/contact"
                            data-testid="how-cta-contact"
                            className="inline-flex items-center gap-3 border border-white/25 text-white px-7 py-4 text-[12px] tracking-[0.26em] uppercase hover:bg-white hover:text-[#05070A] transition-colors duration-300"
                        >
                            Book a conversation <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default HowWeWork;
